function _typeof(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized(self); }

function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { camelize, isUndef, wrapDisplayName } from './utils';
/**
 * A function that returns a React HOC that provides values and corresponding setter functions to the wrapped component.
 * For each variable name given, the wrapped component will receive the following props:
 *
 * - `<variableName>`: the value, default = `null`.
 * - `set<variableName>`: a function that will set the value to a given value.
 *
 * `getSet` also exposes a `getSetPropTypes` function to automatically generate PropTypes for these props.
 *
 * **Options**
 * 
 * `getSet` may be passed an options object containing the following keys:
 * - `initialValues`: An object containing initial values for the variables
 *
 * These options can also be passed in as props to the wrapped component.
 *
 * @name getSet
 * @type Function
 * @param {string|Array} varNames - A variable name or array of variable names
 * @param {object} options - Options for the HOC as specified above.
 * @returns {Function} - A HOC that can be used to wrap a component.
 *
 *
 * @example
 *
 * function TabBar ({ currentTab, setCurrentTab, tabs ... }) {
 *   return (
 *     <div>
 *       { 
 *         tabs.map(tab => 
 *            <Tab 
 *              key={ tab } 
 *              isActive={ tab === currentTab }
 *              onClick={ () => setCurrentTab(tab) }
 *            />
 *         )
 *       }
 *     </div>
 *   )
 * }
 * 
 * TabBar.propTypes = {
 *   ...getSetPropTypes('currentTab'),
 *   tabs: PropTypes.arrayOf(PropTypes.number),
 * }
 *
 * export default compose(
 *    getSet('currentTab', { 
 *      initialValues: { 
 *        currentTab: 1,
 *      },
 *    }),
 * )(TabBar)
 *
*/

function getSet() {
  var names = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  // Transform names to array
  var varNames = Array.isArray(names) ? names : [names];
  return function (WrappedComponent) {
    var _class, _temp;

    return _temp = _class =
    /*#__PURE__*/
    function (_Component) {
      _inherits(Wrapper, _Component);

      /*
       * A friendly name for React devtools and errors
       */

      /*
       * A reference to the wrapped component
       */
      function Wrapper(props) {
        var _this;

        _classCallCheck(this, Wrapper);

        _this = _possibleConstructorReturn(this, _getPrototypeOf(Wrapper).call(this, props));

        var config = _objectSpread({}, props, {}, options);

        _this.state = getInitialState(varNames, config.initialValues);
        _this.set = _this.set.bind(_assertThisInitialized(_this));
        _this.setters = buildSetters(varNames, _this.set);
        return _this;
      }

      _createClass(Wrapper, [{
        key: "set",
        value: function set(varName, value) {
          this.setState(_defineProperty({}, varName, value));
        }
      }, {
        key: "render",
        value: function render() {
          return React.createElement(WrappedComponent, _objectSpread({}, this.props, {}, this.setters, {}, this.state));
        }
      }]);

      return Wrapper;
    }(Component), _defineProperty(_class, "displayName", wrapDisplayName(WrappedComponent, 'getSet')), _defineProperty(_class, "WrappedComponent", WrappedComponent), _temp;
  };
} // Set initial values if defined


function getInitialState(varNames) {
  var initialValues = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var state = {};
  varNames.forEach(function (varName) {
    var initialValue = initialValues[varName];
    state[varName] = isUndef(initialValue) ? null : initialValue;
  });
  return state;
} // Return a hash of setter functions


function buildSetters(varNames, set) {
  var setters = {};
  varNames.forEach(function (varName) {
    setters[setterFuncName(varName)] = function (value) {
      return set(varName, value);
    };
  });
  return setters;
}

function setterFuncName(varName) {
  return camelize(['set', varName].join(' '));
}

export function getSetPropTypes() {
  var names = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var varNames = Array.isArray(names) ? names : [names];
  var propTypes = {};
  varNames.forEach(function (varName) {
    propTypes[varName] = PropTypes.any;
    propTypes[setterFuncName(varName)] = PropTypes.func;
  });
  return propTypes;
}
export default getSet;